import { useTranslation } from "react-i18next"

export type Translations = typeof defaultTranslations

type UseSettingsPageTranslationsParams = {
  name?: string
  member?: string
  email?: string
  orgName?: string
  fullname?: string
  accessLevel?: string
  role?: string
  copyToName?: string
  copyPermissionFromMemberName?: string
}

export const useSettingsPageTranslations = ({
  name = "",
  member = "",
  email = "",
  orgName = "",
  fullname = "",
  accessLevel = "",
  role = "",
  copyToName = "",
  copyPermissionFromMemberName = "",
}: UseSettingsPageTranslationsParams = {}): Translations => {
  const { t } = useTranslation()
  const { t: tMembership } = useTranslation("membership")

  return {
    basicInfo: tMembership("basicInfo", { defaultValue: defaultTranslations.basicInfo }),
    permissionsTabLabel: tMembership("membershipSettingsView.permissionsTabLabel", {
      defaultValue: defaultTranslations.permissionsTabLabel,
    }),
    standardMemberLabel: tMembership("standardMemberLabel", {
      defaultValue: defaultTranslations.standardMemberLabel,
    }),
    limitedMemberLabel: tMembership("limitedMemberLabel", {
      defaultValue: defaultTranslations.limitedMemberLabel,
    }),
    copyFromMemberLabel: tMembership("copyFromMemberLabel", {
      defaultValue: defaultTranslations.copyFromMemberLabel,
    }),
    chooseMemberToCopyLabel: tMembership("chooseMemberToCopyLabel", {
      defaultValue: defaultTranslations.chooseMemberToCopyLabel,
    }),
    searchMembersPlaceholder: tMembership("searchMembersPlaceholder", {
      defaultValue: defaultTranslations.searchMembersPlaceholder,
    }),
    fillBillingInfoText: tMembership("newMemberPage.fillBillingInfoText", {
      defaultValue: defaultTranslations.fillBillingInfoText,
    }),
    clickMeLabel: tMembership("newMemberPage.clickMeLabel", {
      defaultValue: defaultTranslations.clickMeLabel,
    }),
    sendInvitationLabel: tMembership("newMemberPage.sendInvitationLabel", {
      defaultValue: defaultTranslations.sendInvitationLabel,
    }),
    firstnameLabel: tMembership("membershipSettingsView.firstnameLabel", {
      defaultValue: defaultTranslations.firstnameLabel,
    }),
    lastnameLabel: tMembership("membershipSettingsView.lastnameLabel", {
      defaultValue: defaultTranslations.lastnameLabel,
    }),
    emailLabel: tMembership("membershipSettingsView.emailLabel", {
      defaultValue: defaultTranslations.emailLabel,
    }),
    pageHeader: tMembership("membershipSettingsView.pageHeader", {
      defaultValue: defaultTranslations.pageHeader,
      name,
    }),
    managePermissionsOrAssociationsLabel: tMembership("membershipSettingsView.managePermissionsOrAssociationsLabel", {
      defaultValue: defaultTranslations.managePermissionsOrAssociationsLabel,
    }),
    manageAssociationsLabel: tMembership("membershipSettingsView.manageAssociationsLabel", {
      defaultValue: defaultTranslations.manageAssociationsLabel,
    }),
    managePermissionsLabel: tMembership("membershipSettingsView.managePermissionsLabel", {
      defaultValue: defaultTranslations.managePermissionsLabel,
    }),
    noAccessMemberLabel: tMembership("membershipSettingsView.noAccessMemberLabel", {
      defaultValue: defaultTranslations.noAccessMemberLabel,
    }),
    orgOwnerLabel: tMembership("membershipSettingsView.orgOwnerLabel", {
      defaultValue: defaultTranslations.orgOwnerLabel,
    }),
    cancelInvitationLabel: tMembership("membershipSettingsView.cancelInvitationLabel", {
      defaultValue: defaultTranslations.cancelInvitationLabel,
    }),
    memberInvitedMessage: tMembership("membershipSettingsView.memberInvitedMessage", {
      defaultValue: defaultTranslations.memberInvitedMessage,
      orgName,
      fullname,
    }),
    inviteButtonLabel: tMembership("membershipSettingsView.inviteButtonLabel", {
      defaultValue: defaultTranslations.inviteButtonLabel,
      name,
    }),
    invitationInfo: tMembership("invitationInfo", {
      defaultValue: defaultTranslations.invitationInfo,
      email,
      member,
    }),
    invitationDialogTitle: tMembership("membershipSettingsView.invitationDialogTitle", {
      defaultValue: defaultTranslations.invitationDialogTitle,
      name,
      orgName,
    }),
    resetPermissionsLabel: tMembership("membershipSettingsView.resetPermissionsLabel", {
      defaultValue: defaultTranslations.resetPermissionsLabel,
    }),
    resetToStandardMemberLabel: tMembership("membershipSettingsView.resetToStandardMemberLabel", {
      defaultValue: defaultTranslations.resetToStandardMemberLabel,
    }),
    resetToLimitedMemberLabel: tMembership("membershipSettingsView.resetToLimitedMemberLabel", {
      defaultValue: defaultTranslations.resetToLimitedMemberLabel,
    }),
    copyPermissionFromMemberConfirmationDialogText: tMembership(
      "membershipSettingsView.copyPermissionFromMemberConfirmationDialogText",
      {
        defaultValue: defaultTranslations.copyPermissionFromMemberConfirmationDialogText,
      }
    ),
    copyPermissionFromMemberSuccess: tMembership("membershipSettingsView.copyPermissionFromMemberSuccess", {
      defaultValue: defaultTranslations.copyPermissionFromMemberSuccess,
      name: copyPermissionFromMemberName,
    }),
    resetPermissionToLimitedConfirmationDialogText: tMembership(
      "membershipSettingsView.resetPermissionToLimitedConfirmationDialogText",
      {
        defaultValue: defaultTranslations.resetPermissionToLimitedConfirmationDialogText,
      }
    ),
    resetPermissionToLimitedMemberSuccess: tMembership("membershipSettingsView.resetPermissionToLimitedMemberSuccess", {
      defaultValue: defaultTranslations.resetPermissionToLimitedMemberSuccess,
    }),
    resetPermissionToStandardConfirmationDialogText: tMembership(
      "membershipSettingsView.resetPermissionToStandardConfirmationDialogText",
      {
        defaultValue: defaultTranslations.resetPermissionToStandardConfirmationDialogText,
      }
    ),
    resetPermissionToStandardMemberSuccess: tMembership(
      "membershipSettingsView.resetPermissionToStandardMemberSuccess",
      {
        defaultValue: defaultTranslations.resetPermissionToStandardMemberSuccess,
      }
    ),
    normalMembershipHelperText: tMembership("newMemberPage.normalMembershipHelperText", {
      defaultValue: defaultTranslations.normalMembershipHelperText,
    }),
    limitedMembershipHelperText: tMembership("newMemberPage.limitedMembershipHelperText", {
      defaultValue: defaultTranslations.limitedMembershipHelperText,
    }),
    copiedMemberHelperText: tMembership("membershipSettingsView.copiedMemberHelperText", {
      defaultValue: defaultTranslations.copiedMemberHelperText,
      copy_to_name: copyToName,
    }),
    resetButtonLabel: tMembership("membershipSettingsView.resetButtonLabel", {
      defaultValue: defaultTranslations.resetButtonLabel,
    }),
    currentBasePermissionInfo: tMembership("membershipSettingsView.currentBasePermissionInfo", {
      defaultValue: defaultTranslations.currentBasePermissionInfo,
      name,
      role,
    }),
    memberRejectedMessage: tMembership("membershipSettingsView.memberRejectedMessage", {
      defaultValue: defaultTranslations.memberRejectedMessage,
      fullname,
      orgName,
    }),
    inviteAgainButtonLabel: tMembership("membershipSettingsView.inviteAgainButtonLabel", {
      defaultValue: defaultTranslations.inviteAgainButtonLabel,
    }),
    invitationLanguageDescription: tMembership("invitationLanguageDescription", {
      defaultValue: defaultTranslations.invitationLanguageDescription,
    }),
    note: t("note", { defaultValue: defaultTranslations.note }),
    provideValidEmailToInvite: tMembership("provideValidEmailToInvite", {
      defaultValue: defaultTranslations.provideValidEmailToInvite,
    }),
    customizeAccess: tMembership("customizeAccess", {
      defaultValue: defaultTranslations.customizeAccess,
    }),
    accessLevelInfo: tMembership("accessLevelInfo", {
      defaultValue: defaultTranslations.accessLevelInfo,
      member,
      accessLevel,
    }),
    invitationHeader: tMembership("invitationHeader", {
      defaultValue: defaultTranslations.invitationHeader,
      member,
    }),
    memberInvited: tMembership("memberInvited", {
      defaultValue: defaultTranslations.memberInvited,
      member,
    }),
    memberInvitedDescription: tMembership("memberInvitedDescription", {
      defaultValue: defaultTranslations.memberInvitedDescription,
      email,
    }),
    sendInvitation: tMembership("sendInvitation", {
      defaultValue: defaultTranslations.sendInvitation,
    }),
    sendInvitationAgain: tMembership("sendInvitationAgain", {
      defaultValue: defaultTranslations.sendInvitationAgain,
    }),
    ownerAccessInfo: tMembership("ownerAccessInfo", {
      defaultValue: defaultTranslations.ownerAccessInfo,
      member,
    }),
    freePlanLimitHeader: tMembership("freePlanLimitHeader", {
      defaultValue: defaultTranslations.freePlanLimitHeader,
    }),
    freePlanLimitDescription1: tMembership("freePlanLimitDescription1", {
      defaultValue: defaultTranslations.freePlanLimitDescription1,
    }),
    freePlanLimitDescription2: tMembership("freePlanLimitDescription2", {
      defaultValue: defaultTranslations.freePlanLimitDescription2,
    }),
    enterBillingInfo: tMembership("enterBillingInfo", {
      defaultValue: defaultTranslations.enterBillingInfo,
    }),
  }
}

const defaultTranslations = {
  pageHeader: "Settings of {{name}}",
  basicInfo: "Basic information",
  permissionsTabLabel: "Permissions",
  firstnameLabel: "Firstname",
  lastnameLabel: "Lastname",
  emailLabel: "Email",
  fillBillingInfoText: "Please fill in the billing information before sending the invitation!!!",
  clickMeLabel: "Click me",
  sendInvitationLabel: "Send invitation",
  managePermissionsOrAssociationsLabel: "Manage permissions or associations",
  manageAssociationsLabel: "Manage associations",
  managePermissionsLabel: "Manage permissions",
  standardMemberLabel: "Standard member",
  limitedMemberLabel: "Limited member",
  noAccessMemberLabel: "No access",
  orgOwnerLabel: "Organisation owner",
  cancelInvitationLabel: "Cancel invitation",
  memberInvitedMessage: "An invitation to join {{orgName}} has been sent to {{fullname}}",
  memberRejectedMessage: "{{fullname}} has rejected the invitation to join {{orgName}}",
  inviteButtonLabel: "Send invitation",
  inviteAgainButtonLabel: "Send invitation again",
  invitationInfo:
    "We will send an email at {{email}} with a link to join the organisation. {{member}} will be able to access the organisation after accepting the invitation.",
  invitationDialogTitle: "Invite {{name}} to join {{orgName}}?",
  resetPermissionsLabel: "Reset permissions to defaults or copy permissions",
  resetToStandardMemberLabel: "Reset to standard member",
  resetToLimitedMemberLabel: "Reset to limited member",
  copyFromMemberLabel: "Copy permissions",
  copyPermissionFromMemberConfirmationDialogText: "Are you sure you want to copy permissions from the selected member?",
  copyPermissionFromMemberSuccess: "Successfully copied permissions of {{name}}",
  resetPermissionToLimitedConfirmationDialogText: "Are you sure you want to reset permissions to limited member?",
  resetPermissionToLimitedMemberSuccess: "Successfully reset permissions to limited member",
  resetPermissionToStandardConfirmationDialogText: "Are you sure you want to reset permissions to standard member?",
  resetPermissionToStandardMemberSuccess: "Successfully reset permissions to standard member",
  chooseMemberToCopyLabel: "Choose member to copy from",
  searchMembersPlaceholder: "Search members",
  normalMembershipHelperText:
    "A normal member has access to all projects, tasks, to-dos, resources, and other information within the organization. However, specific actions can be restricted by adjusting the member's permissions.",
  limitedMembershipHelperText:
    "A limited member can access their own projects, tasks, to-dos, and other relevant information within the organization. Additionally, they will be able to view resources specified in the input field of this form. Permissions for a limited member operate in the same way as those for a regular member.",
  resetButtonLabel: "Reset permissions",
  currentBasePermissionInfo: `Currently {{name}} has base permissions of {{role}}. If any permission has been modified, it is reflected in the table below.`,
  copiedMemberHelperText: "{{copy_to_name}} will have exact same permissions as the permissions copied member",
  invitationLanguageDescription: "The invitation email will be sent in the selected language",
  note: "Note",
  provideValidEmailToInvite: "Email is required to accept the invitation. Please provide a valid email.",
  customizeAccess: "Customize access level",
  accessLevelInfo:
    "Upon accepting the invitation, {{member}} will have {{accessLevel}} access. You can customize the access level before sending the invitation.",
  invitationHeader: "Invite {{member}} and grant access to this organization",
  memberInvited: "{{member}} is invited to join the organization",
  memberInvitedDescription:
    "An invitation has been sent to {{email}}. They need to accept the invitation to join the organization. You can cancel and resend the invitation if you want to change the email.",
  sendInvitation: "Send invitation",
  sendInvitationAgain: "Send invitation again",
  ownerAccessInfo:
    "As an owner, {{member}} has full access to perform all actions within the organization. Owner's permissions cannot be modified.",
  freePlanLimitHeader: "Free plan limit reached",
  freePlanLimitDescription1: "Starbrix is free for upto 3 members.",
  freePlanLimitDescription2: "To invite a 4th member please enter your billing information.",
  enterBillingInfo: "Enter billing information",
}
